import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`This document describes your options when looking to unpublish a package published to the public registry.`}</p>
    <p>{`Registry data is immutable, meaning once published, a package cannot change. We do this for reasons of security and stability of the users who depend on those packages. So if you've ever published a package called "bob" at version 1.1.0, no other package can ever be published with that name at that version. This is true even if that package is unpublished.`}</p>
    <p>{`However, because accidents happen, we allow you to unpublish packages in the situations described below. Otherwise, you can always deprecate a package.`}</p>
    <h2>{`Packages published less than 72 hours ago`}</h2>
    <p>{`For newly created packages, as long as no other packages in the npm Public Registry depend on your package, you can unpublish anytime within the first 72 hours after publishing.`}</p>
    <h2>{`Packages published more than 72 hours ago`}</h2>
    <p>{`Regardless of how long ago a package was published, you can unpublish a package that:`}</p>
    <ul>
      <li parentName="ul">{`no other packages in the npm Public Registry depend on`}</li>
      <li parentName="ul">{`had less than 300 downloads over the last week`}</li>
      <li parentName="ul">{`has a single owner/maintainer`}</li>
    </ul>
    <h2>{`How to unpublish`}</h2>
    <p>{`To unpublish a single package version, run `}<inlineCode parentName="p">{`npm unpublish <package_name>@<version>`}</inlineCode>{`.`}</p>
    <p>{`If all the versions of a package can be unpublished, you can unpublish all versions at once by running `}<inlineCode parentName="p">{`npm unpublish <package_name> --force`}</inlineCode>{`.`}</p>
    <h2>{`Considerations:`}</h2>
    <ul>
      <li parentName="ul">{`Once `}<inlineCode parentName="li">{`package@version`}</inlineCode>{` has been used, you can never use it again. You must publish a new version even if you unpublished the old one.`}</li>
      <li parentName="ul">{`Once you have unpublished a package, you will not be able to undo the unpublish.`}</li>
      <li parentName="ul">{`If you entirely unpublish all versions of a package, you may not publish any new versions of that package until 24 hours have passed.`}</li>
    </ul>
    <h2>{`What to do if your package does not meet the unpublish criteria?`}</h2>
    <p>{`If your package does not meet the unpublish policy criteria, we recommend `}<a parentName="p" {...{
        "href": "https://docs.npmjs.com/cli/deprecate"
      }}>{`deprecating`}</a>{` the package. This allows the package to be downloaded but publishes a clear warning message (that you get to write) every time the package is downloaded, and on the package's npmjs.com page. Users will know that you do not recommend they use the package, but if they are depending on it their builds will not break. We consider this a good compromise between reliability and author control.`}</p>
    <p>{`This can be achieved by using one of the following from your command line:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`npm deprecate <package> "<message>"`}</inlineCode>{` to deprecate the entire package`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`npm deprecate <package>@<version> "<message>"`}</inlineCode>{` to deprecate a specific version`}</li>
    </ul>
    <p>{`If the entire package is deprecated, the package name will be dropped from our search results.`}</p>
    <p>{`Once deprecated, if you would also like for the package to be removed from your user profile, it can be `}<a parentName="p" {...{
        "href": "https://docs.npmjs.com/cli/owner"
      }}>{`transferred`}</a>{` to our `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/~npm"
      }}>{`@npm`}</a>{` account. This can be achieved by using the following from your command line:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`npm owner add npm <package>`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`npm owner rm <your_username> <package>`}</inlineCode></li>
    </ul>
    <h2>{`More on our unpublish policy`}</h2>
    <p>{`This document is additive to the `}<a parentName="p" {...{
        "href": "https://docs.npmjs.com/unpublishing-packages-from-the-registry"
      }}>{`unpublish procedures`}</a>{`, the CLI commands `}<a parentName="p" {...{
        "href": "https://docs.npmjs.com/cli/unpublish"
      }}>{`unpublish documentation`}</a>{` and the `}<a parentName="p" {...{
        "href": "https://blog.npmjs.org/post/190553543620/changes-to-npmunpublish-policy-january-2020"
      }}>{`"Changes to npm Unpublish Policy - January 2020"`}</a>{` blog post.`}</p>
    <h2>{`Issues?`}</h2>
    <p>{`If for some reason your package meets the unpublish policy criteria but the unpublish command fails, or if you need assistance with the deprecate process, please `}<a parentName="p" {...{
        "href": "https://npmjs.com/support"
      }}>{`reach out to our support team`}</a>{` where we'll be happy to assist.`}</p>
    <p>{`If you believe a package violates npm's terms or policies, such as our terms of use, `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/support"
      }}>{`reach out to our support team`}</a>{`.  If a package infringes your copyright, `}<a parentName="p" {...{
        "href": "/policies/dmca"
      }}>{`refer to npm's DMCA takedown policy`}</a>{`.  If you believe a package violates your privacy rights, `}<a parentName="p" {...{
        "href": "/policies/privacy#contact"
      }}>{`contact our privacy team`}</a>{` as soon as possible.`}</p>
    <h2>{`Changes`}</h2>
    <p>{`This is a living document and may be updated from time to time.
Please refer to the `}<a parentName="p" {...{
        "href": "https://github.com/npm/documentation/blob/main/content/policies/unpublish.mdx"
      }}>{`git history for this
document`}</a>{`
to view the changes.`}</p>
    <h2>{`License`}</h2>
    <p>{`Copyright (C) npm, Inc., All rights reserved`}</p>
    <p>{`This document may be reused under a `}<a parentName="p" {...{
        "href": "https://creativecommons.org/licenses/by-sa/4.0/"
      }}>{`Creative Commons
Attribution-ShareAlike
License`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      